import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, X, Search, User, Calendar } from 'lucide-react';
import './PipelineDealActionsOperation.css';
import API_ENDPOINTS from '../../../config/apiEndpoints';
import Alert from '../../../common/Alerts/Alert';



const formatDateForInput = (dateStr) => {
    if (!dateStr) return new Date().toISOString().split('T')[0];
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return new Date().toISOString().split('T')[0];
    return date.toISOString().split('T')[0];
};

const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    
    let dateObj;
    // Manual parse YYYY-MM-DD to avoid timezone shifts
    const parts = dateStr.split('-');
    if (parts.length === 3 && parts[0].length === 4) {
        dateObj = new Date(parts[0], parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    } else {
        dateObj = new Date(dateStr);
    }

    if (isNaN(dateObj.getTime())) return dateStr;

    const d = String(dateObj.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const m = months[dateObj.getMonth()];
    const y = dateObj.getFullYear();
    
    return `${d}-${m}-${y}`;
};

const PipelineDealActionsOperation = ({ deal, mode = 'edit', onBack, onUpdate }) => {
    const [dealData, setDealData] = useState({
        Amount: deal?.amount || deal?.Amount || '',
        PipelineId: deal?.pipelineId || deal?.PipelineId || deal?.PipelineID || '',
        PipelineStage: deal?.stageId || deal?.StageID || deal?.PipelineStageid || deal?.PipelineStage || '',
        Probability: deal?.probability || deal?.Probability || '10',
        DealOwner: deal?.DealOwnerID || deal?.ownerId || deal?.OwnerID || localStorage.getItem('userId') || '34594',
        ClosureDate: formatDateForInput(deal?.EstimatedClosureDate || deal?.closureDate || deal?.ClosureDate),
        DealID: deal?.LeadPipelinemapID || deal?.dealId || deal?.DealID || deal?.id || '',
        LeadID: deal?.LeadID || deal?.leadId || '0',
        Remarks: deal?.Remarks || deal?.remarks || ''
    });

    const [pipelines, setPipelines] = useState([]);
    const [stages, setStages] = useState([]);
    const [owners, setOwners] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);

    // Lead Search States
    const [leadSearchTerm, setLeadSearchTerm] = useState(deal?.personName || deal?.LeadName || '');
    const [leadResults, setLeadResults] = useState([]);
    const [showLeadDropdown, setShowLeadDropdown] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);
    const dropdownRef = useRef(null);
    const dateInputRef = useRef(null);

    // Alert State
    const [showAlert, setShowAlert] = useState(false);
    const [alertConfig, setAlertConfig] = useState({ type: 'error', title: 'Error', message: '' });

    const TOKEN = API_ENDPOINTS.TOKE.UNIVERSAL_TOKEN;
    const NEW_BASE_URL = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
    const API_BASE = (process.env.REACT_APP_WCF_API_BASE_URL || '').replace(/\/$/, '');
    
    const Pipeline_stage = `${NEW_BASE_URL}${API_ENDPOINTS.COMMON.PIPELINE_STAGE_BASED_ON_DEALPIPELINE}`;
    const DealUpdation = `${(process.env.REACT_APP_SERVICES_API_BASE_URLT || '').replace(/\/$/, '')}${API_ENDPOINTS.PIPELINE.FOR_DEAL_UPDATION}`;
    const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
    const FOR_OWNER = `${NEWV3_BASE_URL}/${API_ENDPOINTS.APPOINTMENTS.GET_USERLIST_PHYSICALAPP_VIEW}`;
    const FOR_LEAD_SEARCH = `${NEWV3_BASE_URL}/${API_ENDPOINTS.TASKS.RELATED_TO_SUGGETION}`;
    const SAVE_DEAL = `${NEW_BASE_URL}${API_ENDPOINTS.COMMON.SAVE_DEAL}`;
    const Get_Pipeline_List = `${NEW_BASE_URL}${API_ENDPOINTS.PIPELINE.GET_PIPELINE_LIST}`;

    const formatToApiDate = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        const day = date.getDate().toString().padStart(2, '0');
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const fetchLeadSearch = useCallback(async (term) => {
        if (!term || term.length < 1) {
            setLeadResults([]);
            setShowLeadDropdown(false);
            return;
        }
        try {
            const uid = localStorage.getItem('userId') || '34594';
            const payload = {
                Token: "-2295521862261168",
                Details: {
                    TaskType: "Lead",
                    FilterText: term,
                    UserId: parseInt(uid)
                }
            };
            const response = await fetch(FOR_LEAD_SEARCH, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify(payload)
            });
            if (response.ok) {
                const result = await response.json();
                const d_res = result.d || result.Details || result;
                let data = [];
                if (typeof d_res === 'string') { try { data = JSON.parse(d_res).Table || JSON.parse(d_res) || []; } catch (e) { } } 
                else { data = d_res.Table || d_res || []; }
                
                setLeadResults(data);
                setShowLeadDropdown(true);
            }
        } catch (error) { console.error("Error searching leads:", error); }
    }, [FOR_LEAD_SEARCH]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowLeadDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchStages = useCallback(async (pipelineId) => {
        if (!pipelineId) return;
        try {
            const payload = { Token: TOKEN, Details: Number(pipelineId) };
            const response = await fetch(Pipeline_stage, {
                method: 'POST',
                headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
                body: JSON.stringify(payload)
            });
            if (response.ok) {
                const result = await response.json();
                const d_res = result.d || result.Details || result;
                let data = [];
                if (typeof d_res === 'string') { try { data = JSON.parse(d_res).Table || JSON.parse(d_res) || []; } catch (e) { } } 
                else { data = d_res.Table || d_res || []; }
                
                if (Array.isArray(data)) {
                    setStages(data.map(s => ({ id: (s.Id || s.Code || s.StageID || '').toString(), name: s.Name || s.Text || s.StageName || '' })));
                }
            }
        } catch (error) { console.error("Error fetching stages:", error); }
    }, [TOKEN, Pipeline_stage]);

    const fetchPipelines = useCallback(async () => {
        try {
            const uid = localStorage.getItem('userId') || '34594';
            const payload = {
                Token: TOKEN.toString(),
                Details: parseInt(uid, 10)
            };
            const response = await fetch(Get_Pipeline_List, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (response.ok) {
                const result = await response.json();
                const data = result?.Details || result?.d || [];
                if (Array.isArray(data) && data.length > 0) {
                    const mapped = data.map(p => ({
                        id: (p.PipeLineID || p.PipeLineId || p.pipelineId || p.Id || p.id || p.Code || '').toString(),
                        name: p.PipelineName || p.Pipeline || p.name || p.Name || p.Text || ''
                    }));
                    setPipelines(mapped);
                }
            }
        } catch (error) { console.error("Error fetching pipelines:", error); }
    }, [Get_Pipeline_List, TOKEN]);

    const fetchOwners = useCallback(async () => {
        try {
            const uid = localStorage.getItem('userId') || '34594';
            const response = await fetch(FOR_OWNER, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify({ Token: "-2295521862261168", Details: { UserId: parseInt(uid, 10) } })
            });
            if (response.ok) {
                const result = await response.json();
                const data = result.Details?.data || result.Details || result.d || result;
                let finalUsers = [];
                if (Array.isArray(data)) finalUsers = data; 
                else if (typeof data === 'string') { try { const parsed = JSON.parse(data); finalUsers = parsed?.Table || (Array.isArray(parsed) ? parsed : []); } catch (e) { } }
                else if (data?.Table) finalUsers = data.Table;
                
                if (finalUsers.length > 0) {
                    setOwners(finalUsers.map(owner => ({
                        id: (owner.UserId || owner.User_ID || owner.ID || owner.id || owner.Code || owner.Item1 || owner.Value || '').toString(),
                        name: owner.UserName || owner.FirstName || owner.Name || owner.Text || owner.Item2 || owner.Label || ''
                    })));
                }
            }
        } catch (error) { console.error("Error fetching owners:", error); }
    }, [FOR_OWNER]);

    // Capture the initial pipeline ID from the deal prop on mount
    const initialPipelineId = deal?.pipelineId || deal?.PipelineId || deal?.PipelineID || '';

    useEffect(() => {
        const init = async () => {
            setIsLoadingData(true);
            await fetchPipelines();
            await fetchOwners();
            if (initialPipelineId) {
                await fetchStages(initialPipelineId);
            }
            setIsLoadingData(false);
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'Probability') {
            if (Number(value) > 100) {
                setAlertConfig({
                    type: 'error',
                    title: 'Invalid Probability',
                    message: 'Probability cannot be more than 100%'
                });
                setShowAlert(true);
                return;
            }
        }

        setDealData(prev => ({ ...prev, [name]: value }));
        if (name === 'PipelineId') {
            fetchStages(value);
        }
    };

    const handleLeadSearchChange = (e) => {
        const val = e.target.value;
        setLeadSearchTerm(val);
        fetchLeadSearch(val);
    };

    const handleSelectLead = (lead) => {
        setLeadSearchTerm(lead.LeadName || lead.Text || lead.Name);
        setSelectedLead(lead);
        setShowLeadDropdown(false);
        // Be extremely aggressive with ID keys
        const leadId = (
            lead.Value || 
            lead.Id || 
            lead.id || 
            lead.ID || 
            lead.LeadID || 
            lead.leadId || 
            lead.Lead_Id || 
            lead.Code || 
            lead.EnquiryID || 
            lead.enquiryId || 
            '0'
        ).toString();
        setDealData(prev => ({ ...prev, LeadID: leadId }));
    };

    const handleSave = async () => {
        if (!dealData.Amount || !dealData.PipelineId || !dealData.PipelineStage || !dealData.ClosureDate) {
            setAlertConfig({
                type: 'error',
                title: 'Missing Fields',
                message: 'Please fill all required fields marked with *'
            });
            setShowAlert(true);
            return;
        }

        // Date Validation: Ensure ClosureDate is not in the past
        const selectedDate = new Date(dealData.ClosureDate);
        selectedDate.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (selectedDate < today) {
            setAlertConfig({
                type: 'error',
                title: 'Invalid Date',
                message: 'Estimated Closure Date cannot be in the past. Please select today or a future date.'
            });
            setShowAlert(true);
            return;
        }

        const currentLeadId = (
            selectedLead?.Value || 
            selectedLead?.Id || 
            selectedLead?.id || 
            selectedLead?.ID || 
            selectedLead?.LeadID || 
            selectedLead?.leadId || 
            selectedLead?.Lead_Id || 
            selectedLead?.Code || 
            selectedLead?.EnquiryID || 
            selectedLead?.enquiryId || 
            dealData.LeadID
        );

        if (mode === 'add' && (currentLeadId === '0' || !currentLeadId)) {
            setAlertConfig({
                type: 'error',
                title: 'Lead Missing',
                message: 'Please search and select a lead for this deal.'
            });
            setShowAlert(true);
            return;
        }

        setIsSubmitting(true);
        try {
            const uid = (localStorage.getItem('userId') || '34594').toString();
            let payload;
            let endpoint;

            // Both add and edit use the same SaveDealData endpoint.
            // For edit, LeadPipelinemapID is passed so the backend performs an UPDATE.
            endpoint = SAVE_DEAL;
            payload = {
                Status: "",
                Message: "",
                Token: TOKEN,
                Details: {
                    LeadId: currentLeadId.toString(),
                    PipelineId: Number(dealData.PipelineId),
                    PipelineStage: Number(dealData.PipelineStage),
                    Amount: Number(dealData.Amount),
                    ClosureDate: formatToApiDate(dealData.ClosureDate),
                    Probability: Number(dealData.Probability),
                    DealOwner: String(dealData.DealOwner),
                    Remarks: dealData.Remarks || "",
                    ...(mode === 'edit' && dealData.DealID
                        ? { LeadPipelinemapID: Number(dealData.DealID) }
                        : {})
                }
            };


            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const result = await response.json();
                if (result.Status === 1 || result.d === "Success" || result.Success || result.d?.toString().toLowerCase().includes("success")) {
                    setAlertConfig({
                        type: 'success',
                        title: 'Success',
                        message: mode === 'add' ? 'Deal saved successfully!' : 'Deal updated successfully!'
                    });
                    setShowAlert(true);
                } else {
                    setAlertConfig({
                        type: 'error',
                        title: 'Error',
                        message: result.Message || result.d || "Failed to process deal"
                    });
                    setShowAlert(true);
                }
            } else {
                setAlertConfig({
                    type: 'error',
                    title: 'Error',
                    message: "Server error occurred."
                });
                setShowAlert(true);
            }
        } catch (error) {
            console.error("Error processing deal:", error);
            setAlertConfig({
                type: 'error',
                title: 'Error',
                message: "Network error occurred."
            });
            setShowAlert(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAlertConfirm = () => {
        setShowAlert(false);
        if (alertConfig.type === 'success') {
            onUpdate && onUpdate();
            onBack && onBack();
        }
    };

    return (
        <div className="premium-operation-container">
            <div className="premium-operation-header">
                <button className="premium-back-btn" onClick={onBack}>
                    <ChevronLeft size={20} />
                </button>
                <div className="premium-header-text">
                    <h2 className="premium-title">{mode === 'add' ? 'ADD NEW Deal' : 'Edit Deal'}</h2>
                    <span className="premium-subtitle">{mode === 'add' ? '' : (deal?.personName || deal?.LeadName || 'Deal Details')}</span>
                </div>
            </div>

            <div className="premium-operation-content">
                <div className="premium-card">
                    <div className="premium-card-body">
                        <div className="premium-form-grid">
                            {/* Lead Search Field */}
                            <div className="premium-form-row">
                                <label className="premium-label">LeadSearch</label>
                                <div className="lead-search-wrapper" ref={dropdownRef}>
                                    <div className="premium-input-wrapper" style={{ position: 'relative' }}>
                                        <input 
                                            type="text" 
                                            value={leadSearchTerm} 
                                            onChange={handleLeadSearchChange} 
                                            className="premium-input" 
                                            placeholder="Search Lead..." 
                                            onFocus={() => leadSearchTerm && setShowLeadDropdown(true)}
                                        />
                                        <Search size={16} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
                                    </div>
                                    {showLeadDropdown && leadResults.length > 0 && (
                                        <div className="lead-dropdown">
                                            {leadResults.map((lead, idx) => (
                                                <div key={idx} className="lead-item" onClick={() => handleSelectLead(lead)}>
                                                    <img 
                                                        src={lead.Avatar || lead.image || "https://kit19.com/assets/custom/img/img_avatar1.png"} 
                                                        alt="" 
                                                        className="lead-avatar" 
                                                        onError={(e) => { e.target.src = "https://kit19.com/assets/custom/img/img_avatar1.png"; }}
                                                    />
                                                    <div className="lead-info">
                                                        <div className="lead-name">{lead.LeadName || lead.Text || lead.Name}</div>
                                                        <div className="lead-phone">{lead.MobileNo || lead.Phone || lead.Email || ""}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="premium-form-row">
                                <label className="premium-label">Deal value <span className="required-star">*</span></label>
                                <div className="premium-input-wrapper">
                                    <input 
                                        type="number" 
                                        name="Amount" 
                                        value={dealData.Amount} 
                                        onChange={handleChange} 
                                        className="premium-input" 
                                        placeholder="Enter Amount" 
                                    />
                                </div>
                            </div>

                            <div className="premium-form-row">
                                <label className="premium-label">Deal Pipeline</label>
                                <select 
                                    name="PipelineId" 
                                    value={dealData.PipelineId} 
                                    onChange={handleChange} 
                                    className="premium-select"
                                >
                                    <option value="">--Select Pipeline--</option>
                                    {pipelines.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="premium-form-row">
                                <label className="premium-label">Deal Stage <span className="required-star">*</span></label>
                                <select 
                                    name="PipelineStage" 
                                    value={dealData.PipelineStage} 
                                    onChange={handleChange} 
                                    className="premium-select"
                                >
                                    <option value="">--Select Stage--</option>
                                    {stages.map(s => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="premium-form-row">
                                <label className="premium-label">Probability (%) <span className="required-star">*</span></label>
                                <input 
                                    type="number" 
                                    name="Probability" 
                                    value={dealData.Probability} 
                                    onChange={handleChange} 
                                    className="premium-input" 
                                    placeholder="Enter Probability" 
                                />
                            </div>



                            <div className="premium-form-row">
                                <label className="premium-label">Deal owner</label>
                                <select 
                                    name="DealOwner" 
                                    value={dealData.DealOwner} 
                                    onChange={handleChange} 
                                    className="premium-select"
                                >
                                    {owners.map(owner => (
                                        <option key={owner.id} value={owner.id || owner.UserId || owner.Id || owner.Code}>{owner.name || owner.UserName || owner.Text || owner.DisplayName}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="premium-form-row">
                                <label className="premium-label">Estimated Closure Date <span className="required-star">*</span></label>
                                <div className="premium-input-wrapper" style={{ position: 'relative' }}>
                                    <input 
                                        type="text" 
                                        value={formatDisplayDate(dealData.ClosureDate)} 
                                        readOnly
                                        className="premium-input" 
                                        placeholder="Select Date"
                                        onClick={() => dateInputRef.current.showPicker && dateInputRef.current.showPicker()}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    <Calendar 
                                        size={18} 
                                        style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', cursor: 'pointer' }} 
                                        onClick={() => dateInputRef.current.showPicker && dateInputRef.current.showPicker()}
                                    />
                                    <input 
                                        type="date" 
                                        ref={dateInputRef}
                                        name="ClosureDate" 
                                        value={dealData.ClosureDate} 
                                        onChange={handleChange} 
                                        min={new Date().toISOString().split('T')[0]}
                                        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', top: 0, left: 0, width: '100%', height: '100%' }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="premium-footer">
                            <button className="premium-btn-secondary" onClick={onBack}>
                                CANCEL
                            </button>
                            <button 
                                className="premium-btn-primary" 
                                onClick={handleSave} 
                                disabled={isSubmitting || isLoadingData}
                            >
                                {isSubmitting ? (mode === 'add' ? 'SAVING...' : 'UPDATING...') : (mode === 'add' ? 'SAVE' : 'UPDATE')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <Alert 
                show={showAlert}
                type={alertConfig.type}
                title={alertConfig.title}
                message={alertConfig.message}
                onConfirm={handleAlertConfirm}
            />
        </div>
    );
};

export default PipelineDealActionsOperation;
